import React from "react";
import { RxCross2 } from "react-icons/rx";
import { TiUserDeleteOutline } from "react-icons/ti";
import { motion, AnimatePresence } from "framer-motion";

const VARIANT_COLORS = {
  primary: {
    iconWrap: "bg-blue-500/20 border-blue-300/40",
    iconColor: "text-blue-100",
    iconAccent: "#2563eb",
    buttonGradient: "linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)",
  },
  secondary: {
    iconWrap: "bg-slate-500/20 border-slate-300/40",
    iconColor: "text-slate-100",
    iconAccent: "#475569",
    buttonGradient: "linear-gradient(90deg, #475569 0%, #334155 100%)",
  },
  success: {
    iconWrap: "bg-emerald-500/20 border-emerald-300/40",
    iconColor: "text-emerald-100",
    iconAccent: "#059669",
    buttonGradient: "linear-gradient(90deg, #059669 0%, #047857 100%)",
  },
  error: {
    iconWrap: "bg-rose-500/20 border-rose-300/40",
    iconColor: "text-rose-100",
    iconAccent: "#e11d48",
    buttonGradient: "linear-gradient(90deg, #e11d48 0%, #be123c 100%)",
  },
  info: {
    iconWrap: "bg-cyan-500/20 border-cyan-300/40",
    iconColor: "text-cyan-100",
    iconAccent: "#0891b2",
    buttonGradient: "linear-gradient(90deg, #0891b2 0%, #0e7490 100%)",
  },
  warning: {
    iconWrap: "bg-amber-500/20 border-amber-300/40",
    iconColor: "text-amber-100",
    iconAccent: "#d97706",
    buttonGradient: "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
  },
};

const MessageDialog = ({
  open,
  onClose,
  title,
  message,
  okText = "OK",
  variant = "primary",
  onConfirm,
}) => {
  const variantClass = VARIANT_COLORS[variant] || VARIANT_COLORS.primary;
  const isError = variant === "error";
  const heading = title || (isError ? "Confirm Delete" : "Confirmation");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/55 backdrop-blur-sm px-4"
          style={{ zIndex: 99999 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full max-w-md mx-auto rounded-2xl shadow-2xl relative overflow-hidden border border-transparent backdrop-blur-md"
            style={{
              background:
                "linear-gradient(155deg, rgba(8, 17, 36, 0.95) 0%, rgba(18, 32, 62, 0.88) 100%) padding-box, linear-gradient(125deg, rgba(56, 189, 248, 0.85) 0%, rgba(59, 130, 246, 0.85) 45%, rgba(99, 102, 241, 0.85) 100%) border-box",
            }}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
            <div className="px-6 pt-6 pb-3 relative">
              <button
                onClick={onClose}
                className="absolute right-4 top-4 text-white transition rounded-full h-8 w-8 grid place-items-center shadow-sm hover:brightness-105"
                style={{ backgroundImage: "linear-gradient(90deg, #f43f5e 0%, #dc2626 100%)" }}
                aria-label="Close dialog"
              >
                <RxCross2 size={16} />
              </button>
              <motion.div
                className={`mx-auto h-14 w-14 rounded-2xl border grid place-items-center ${variantClass.iconWrap}`}
                animate={{ y: [0, -2, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <motion.div
                  animate={{ scale: [1, 1.08, 1], rotate: [0, -6, 6, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <TiUserDeleteOutline
                    className="text-[32px]"
                    style={{ color: variantClass.iconAccent }}
                  />
                </motion.div>
              </motion.div>
              <h2 className="mt-3 text-center text-xl font-bold text-white">{heading}</h2>
            </div>

            <div className="px-6 pt-1 pb-4">
              <p className="text-center text-white text-[15px] leading-relaxed">
                {message}
              </p>
              {isError && (
                <p
                  className="text-xs font-semibold text-white text-center mt-3 border border-rose-300 rounded-lg px-3 py-1.5 mx-auto w-fit shadow-sm"
                  style={{ backgroundImage: "linear-gradient(90deg, #f43f5e 0%, #dc2626 100%)" }}
                >
                  This action cannot be undone.
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/15 flex justify-center gap-3 bg-white/[0.04]">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundImage: "linear-gradient(90deg, #64748b 0%, #334155 100%)" }}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-5 py-2.5 rounded-xl text-white font-semibold shadow-sm transition hover:opacity-90"
                style={{ backgroundImage: variantClass.buttonGradient }}
              >
                {okText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MessageDialog;
